// Cascade-screening candidate selection (pure). Consumes the analyzer's
// per-disease cascade candidates (analysis.family.cascadeCandidates) and applies
// the toolbar's threshold + exclusion filters. It does NOT compute probabilities
// — those come from detectCascadeCandidates, which already excludes affected
// individuals, confirmed/obligate carriers, deceased and dummy nodes. The filters
// here are the ones that aren't already baked in: a probability threshold,
// minors (the detector includes them flagged `deferReason:'minor'`), and
// already-tested-NEGATIVE individuals (the detector only drops positives).

export const THRESHOLDS = Object.freeze([
  { key: 'p50', labelKey: '≥50%', min: 0.5 },
  { key: 'p25', labelKey: '≥25%', min: 0.25 },
  { key: 'any', labelKey: 'Any non-zero', min: 0 }
]);

// A genotype carrier-status value that represents a definitive (confirmed)
// result — i.e. the individual has been tested. Empty / 'None' = not tested.
const CONFIRMED_STATUSES = new Set([
  'carrier',
  'non-carrier',
  'heterozygote',
  'homozygote-or-compound-heterozygote',
  'homozygous-normal'
]);

const normGene = (g) =>
  String(g || '')
    .split(' (')[0]
    .trim()
    .toUpperCase();

// True if the member has a confirmed genotype result for `gene` (any definitive
// carrier status). With `gene` null/empty, matches any confirmed genotype.
// (Confirmed carriers are already excluded upstream, so in practice this catches
// tested-negative individuals.)
export function memberHasGenotypeResult(member, gene) {
  const wanted = gene ? normGene(gene) : null;
  return ((member && member.genotypes) || []).some((g) => {
    const status = String((g && g.carrierStatus) || '').trim();
    if (!CONFIRMED_STATUSES.has(status)) return false;
    if (!wanted) return true;
    return normGene(g.symbol || g.gene) === wanted;
  });
}

// Filter the candidate list for one disease + threshold + exclusions. Returns the
// matching cascade-candidate objects (callers map to pids).
export function selectCascadeCandidates({
  cascadeCandidates = [],
  diseaseId = null,
  minProbability = 0.5,
  excludeMinors = false,
  excludeAlreadyTested = true,
  memberByPid = {}
} = {}) {
  return cascadeCandidates.filter((c) => {
    if (!c) return false;
    if (diseaseId && c.diseaseId !== diseaseId) return false;
    const p = c.priorProbability || 0;
    if (!(p > 0 && p >= minProbability)) return false;
    if (excludeMinors && c.deferReason === 'minor') return false;
    if (excludeAlreadyTested) {
      const m = memberByPid[String(c.pid)];
      if (m && memberHasGenotypeResult(m, c.variant && c.variant.gene))
        return false;
    }
    return true;
  });
}
